// Utility functions for theme-related styling

/**
 * Get button border radius based on theme button style
 * @param {string} buttonStyle - The button style from theme ('square', 'rounded', 'pill')
 * @returns {string} - CSS border radius value
 */
export const getButtonBorderRadius = (buttonStyle) => {
  switch (buttonStyle) {
    case "square":
      return "0px"
    case "rounded":
      return "12px"
    case "pill":
      return "32px"
    default:
      return "12px" // Default to rounded
  }
}

/**
 * Get button classes based on theme button style
 * @param {string} buttonStyle - The button style from theme ('square', 'rounded', 'pill')
 * @returns {string} - Tailwind CSS classes for border radius
 */
export const getButtonClasses = (buttonStyle) => {
  switch (buttonStyle) {
    case "square":
      return "rounded-none"
    case "rounded":
      return "rounded-xl"
    case "pill":
      return "rounded-full"
    default:
      return "rounded-xl" // Default to rounded
  }
}

/**
 * Apply theme button styling to a style object
 * @param {Object} baseStyle - Base style object
 * @param {Object} theme - Theme object containing buttonStyle and borderRadius
 * @returns {Object} - Style object with applied button styling
 */
export const applyButtonTheme = (baseStyle, theme) => {
  const borderRadius = theme?.borderRadius || getButtonBorderRadius(theme?.buttonStyle)

  return {
    ...baseStyle,
    borderRadius: borderRadius
  }
}

/**
 * Get CSS classes for theme-based border radius for visual elements
 * @param {Object} theme - Theme object containing buttonStyle and borderRadius
 * @returns {string} - Tailwind CSS classes for border radius
 */
export const getThemeShapeClasses = (theme) => {
  const buttonStyle = theme?.buttonStyle
  switch (buttonStyle) {
    case "square":
      return "rounded-none"
    case "rounded":
      return "rounded-xl"
    case "pill":
      return "rounded-[32px]"
    default:
      return "rounded-xl" // Default to rounded
  }
}

/**
 * Get CSS border radius value from theme for inline styles
 * @param {Object} theme - Theme object containing buttonStyle and borderRadius
 * @returns {string} - CSS border radius value
 */
export const getThemeBorderRadius = (theme) => theme?.borderRadius || getButtonBorderRadius(theme?.buttonStyle)

/**
 * Apply theme shape styling to any visual element
 * @param {Object} baseStyle - Base style object
 * @param {Object} theme - Theme object containing buttonStyle and borderRadius
 * @returns {Object} - Style object with applied theme border radius
 */
export const applyThemeShape = (baseStyle, theme) => {
  const borderRadius = getThemeBorderRadius(theme)

  return {
    ...baseStyle,
    borderRadius: borderRadius
  }
}
